// Live chat rooms by instrument — static part of the lobby (/live).
// Rooms are keyed by the instrument symbol as used everywhere else in the app (XAUUSD, NAS100,
// BTCUSD…). The pinned rooms below are always listed; any other instrument room shows up in
// the lobby while it has been active in the last 24h (LiveRoom table, see list_live_rooms()).

export type LiveMarket = 'TRADER' | 'COMMODITIES' | 'FOREX' | 'INDICES' | 'CRYPTO' | 'STOCKS';

export const MARKET_ORDER: LiveMarket[] = ['TRADER', 'COMMODITIES', 'FOREX', 'INDICES', 'CRYPTO', 'STOCKS'];

/** English labels (run through t()). */
export const MARKET_LABEL: Record<LiveMarket, string> = {
  TRADER: 'Verified traders',
  COMMODITIES: 'Metals & commodities',
  FOREX: 'Forex',
  INDICES: 'Indices',
  CRYPTO: 'Crypto',
  STOCKS: 'Stocks',
};

/** Pinned rooms: always visible in the lobby, in this order within their market. */
export const PINNED_ROOMS: { symbol: string; market: LiveMarket; label?: string }[] = [
  { symbol: 'XAUUSD', market: 'COMMODITIES', label: 'Gold' },
  { symbol: 'XAGUSD', market: 'COMMODITIES', label: 'Silver' },
  { symbol: 'USOIL', market: 'COMMODITIES', label: 'Oil (WTI)' },
  { symbol: 'EURUSD', market: 'FOREX' },
  { symbol: 'GBPUSD', market: 'FOREX' },
  { symbol: 'USDJPY', market: 'FOREX' },
  { symbol: 'GBPJPY', market: 'FOREX' },
  { symbol: 'NAS100', market: 'INDICES', label: 'Nasdaq 100' },
  { symbol: 'US500', market: 'INDICES', label: 'S&P 500' },
  { symbol: 'US30', market: 'INDICES', label: 'Dow Jones' },
  { symbol: 'GER40', market: 'INDICES', label: 'DAX' },
  { symbol: 'BTCUSD', market: 'CRYPTO', label: 'Bitcoin' },
  { symbol: 'ETHUSD', market: 'CRYPTO', label: 'Ethereum' },
  { symbol: 'SOLUSD', market: 'CRYPTO', label: 'Solana' },
];

const PINNED_BY_SYMBOL = new Map(PINNED_ROOMS.map((r) => [r.symbol, r]));

export const SYMBOL_RE = /^[A-Z0-9]{3,12}$/;

/** "xau/usd" → "XAUUSD"; anything that isn't a plausible symbol → null. */
export function normalizeSymbol(raw: string): string | null {
  const s = (raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return SYMBOL_RE.test(s) ? s : null;
}

const CCY = '(XAU|XAG|EUR|GBP|USD|JPY|CHF|AUD|NZD|CAD|SEK|NOK|TRY|MXN|ZAR|PLN|HUF|CZK)';
const PAIR_RE = new RegExp('^' + CCY + CCY + '$');
const CRYPTO_BASES = ['BTC', 'ETH', 'SOL', 'XRP', 'ADA', 'DOGE', 'BNB', 'DOT', 'AVAX', 'LINK', 'LTC', 'TRX', 'TON', 'SHIB', 'PEPE', 'SUI', 'ARB', 'OP', 'APT', 'NEAR', 'ATOM', 'UNI', 'XLM', 'HBAR'];
const INDEX_RE = /^(NAS100|US100|USTEC|US500|SPX500|US30|DJ30|GER40|GER30|DAX|UK100|JPN225|FRA40|EU50|AUS200|HK50)$/;
const COMMODITY_RE = /^(XAU|XAG|USOIL|UKOIL|WTI|BRENT|NATGAS|XPT|XPD|COPPER|GOLD|SILVER)/;

export function guessMarket(symbol: string): LiveMarket {
  const pinned = PINNED_BY_SYMBOL.get(symbol);
  if (pinned) return pinned.market;
  if (COMMODITY_RE.test(symbol)) return 'COMMODITIES';
  if (INDEX_RE.test(symbol)) return 'INDICES';
  if (symbol.endsWith('USDT') || CRYPTO_BASES.some((b) => symbol === b + 'USD' || symbol === b + 'USDT')) return 'CRYPTO';
  if (PAIR_RE.test(symbol)) return 'FOREX';
  return 'STOCKS';
}

/** Friendly name for pinned rooms (Gold, DAX…), else the symbol itself. */
export function roomLabel(symbol: string): string | null {
  return PINNED_BY_SYMBOL.get(symbol)?.label ?? null;
}

export function isPinnedRoom(symbol: string): boolean {
  return PINNED_BY_SYMBOL.has(symbol);
}

/** Lobby URL for a room; `null` = the general room. */
export function liveRoomHref(symbol: string | null, extra?: Record<string, string>): string {
  const q = new URLSearchParams({ room: symbol ?? 'general', ...(extra ?? {}) });
  return `/live?${q.toString()}`;
}
